import React, { useState } from 'react';
import { cx } from '../cx';

// D-09 §15.40 — 블록 캔버스 (Block Canvas) · WYSIWYG 편집 행
// 표현 전용: 블록 모델·드래그 순서·저장·프리셋·팔레트는 소비처가 소유하고,
// 여기엔 한 행의 사진/컬럼/캡션과 조작 콜백만 넘긴다(도메인·API 무결합).
export type CanvasPhotoId = number | string;

export interface CanvasPhoto {
  id: CanvasPhotoId;
  url: string;
  alt?: string;
}

export interface CanvasPhotoRowProps extends React.HTMLAttributes<HTMLDivElement> {
  photos: CanvasPhoto[];
  /** 현재 열 수(1..maxColumns). 빈 슬롯은 columns - photos.length 만큼 그려진다. */
  columns: number;
  caption?: string;
  onColumns: (delta: number) => void;
  onRemovePhoto: (id: CanvasPhotoId) => void;
  onDropPhoto: () => void;
  onCaption: (value: string) => void;
  onRemoveRow: () => void;
  onDragStart?: () => void;
  maxColumns?: number;
  slotLabel?: string;
  captionPlaceholder?: string;
  /** 사진 렌더 커스텀(예: 인증 blob 로더). 미지정 시 <img src={url}> */
  renderPhoto?: (photo: CanvasPhoto) => React.ReactNode;
}

/** 캔버스의 사진 한 줄 — 카드 없이 사진 흐름 + hover 컨트롤(순서 핸들·열 ±·설명·삭제). */
export function CanvasPhotoRow({
  photos,
  columns,
  caption,
  onColumns,
  onRemovePhoto,
  onDropPhoto,
  onCaption,
  onRemoveRow,
  onDragStart,
  maxColumns = 3,
  slotLabel = '사진 끌어놓기',
  captionPlaceholder = '사진 설명 추가',
  renderPhoto,
  className = '',
  ...props
}: CanvasPhotoRowProps) {
  const [showCaption, setShowCaption] = useState((caption ?? '').length > 0);
  const cols = Math.max(1, Math.min(maxColumns, columns || 1));
  const slots = Array.from({ length: cols }, (_, i) => photos[i] ?? null);

  return (
    <div className={cx('canvas-row', className)} {...props}>
      <div className="canvas-ctrl">
        {onDragStart != null && (
          <span className="canvas-drag" draggable onDragStart={onDragStart} title="드래그로 순서 이동">
            ⠿
          </span>
        )}
        <span className="canvas-cols">
          <button type="button" className="canvas-ctrl-btn" onClick={() => onColumns(-1)} disabled={cols <= 1}>
            −
          </button>
          {cols}열
          <button type="button" className="canvas-ctrl-btn" onClick={() => onColumns(1)} disabled={cols >= maxColumns}>
            +
          </button>
        </span>
        {!showCaption && (
          <button
            type="button"
            className="canvas-ctrl-btn wide"
            onClick={() => setShowCaption(true)}
            title="사진 설명 추가"
          >
            ＋설명
          </button>
        )}
        <button type="button" className="canvas-ctrl-btn" onClick={onRemoveRow} aria-label="줄 삭제">
          ×
        </button>
      </div>

      <div className={cx('canvas-grid', `cols-${cols}`)}>
        {slots.map((p, i) =>
          p ? (
            <div key={p.id} className="canvas-cell">
              {renderPhoto ? renderPhoto(p) : <img src={p.url} alt={p.alt ?? ''} />}
              <button
                type="button"
                className="canvas-cell-x"
                onClick={() => onRemovePhoto(p.id)}
                aria-label="사진 빼기"
              >
                ×
              </button>
            </div>
          ) : (
            <div
              key={`empty-${i}`}
              className="canvas-slot"
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDropPhoto}
            >
              {slotLabel}
            </div>
          ),
        )}
      </div>

      {showCaption && (
        <input
          className="canvas-caption"
          value={caption ?? ''}
          onChange={(e) => onCaption(e.target.value)}
          placeholder={captionPlaceholder}
          autoFocus
        />
      )}
    </div>
  );
}

export interface CanvasTextRowProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  value: string;
  onChange: (value: string) => void;
  /** 없으면 삭제 버튼을 그리지 않는다(사진에 종속된 문단) */
  onRemove?: () => void;
  onDragStart?: () => void;
  placeholder?: string;
  rows?: number;
}

/** 캔버스의 텍스트 문단 — 상시 보이는 입력창. 종속이면(onRemove·onDragStart 없음) 컨트롤바 없이 문단만. */
export function CanvasTextRow({
  value,
  onChange,
  onRemove,
  onDragStart,
  placeholder = '문단을 입력하세요',
  rows = 3,
  className = '',
  ...props
}: CanvasTextRowProps) {
  const showControls = onRemove != null || onDragStart != null;
  return (
    <div className={cx('canvas-text-row', className)} {...props}>
      {showControls && (
        <div className="canvas-ctrl">
          {onDragStart != null && (
            <span className="canvas-drag" draggable onDragStart={onDragStart} title="드래그로 순서 이동">
              ⠿
            </span>
          )}
          {onRemove != null && (
            <button type="button" className="canvas-ctrl-btn" onClick={onRemove} aria-label="줄 삭제">
              ×
            </button>
          )}
        </div>
      )}
      <textarea
        className="canvas-text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
      />
    </div>
  );
}
